// AppBrandMenu — shared branded wordmark dropdown used by viewer (SourceMenu)
// and studio (BrandMenu). One floating-ui implementation, two consumers.
//
// Structure:
//   <AppBrandMenu sections={[...]} />                → "ArchLang"
//   …plus tagline="Studio" to name the surface  → "ArchLang Studio"
//
// Items use @archlang/ui MenuItem for keyboard navigation + portal rendering.
// Consumers build typed sections; custom rows use kind:"custom" + render().

import { Fragment, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cx, Menu, MenuButton, MenuItems, MenuItem } from "@archlang/ui";
import type { Placement } from "@floating-ui/react";
import { AppWordmark } from "./AppWordmark";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AppBrandMenuSection {
  /** Optional group label above items. ReactNode to support inline badges. */
  readonly label?: ReactNode;
  readonly items: readonly AppBrandMenuItem[];
}

export interface AppBrandMenuItemStd {
  readonly kind?: "item";
  readonly key?: string;
  /** Icon (16-18 px, aria-hidden). */
  readonly icon?: ReactNode;
  /** Primary label. */
  readonly label: ReactNode;
  /** Trailing slot — badge, kbd hint, checkmark, etc. */
  readonly right?: ReactNode;
  readonly onClick: () => void;
  /** "danger" = red, "accent" = arch-400. Default is slate-300. */
  readonly variant?: "default" | "accent" | "danger";
  readonly disabled?: boolean;
  /** Keep menu open after click (for items that show inline status). */
  readonly keepOpen?: boolean;
}

export interface AppBrandMenuItemCustom {
  readonly kind: "custom";
  readonly key: string;
  /** Rendered inside MenuItems context — may use @archlang/ui MenuItem for nav. */
  readonly render: () => ReactNode;
}

export type AppBrandMenuItem = AppBrandMenuItemStd | AppBrandMenuItemCustom;

export interface AppBrandMenuProps {
  /** Names this surface beside the wordmark — see AppWordmark, which draws it. */
  readonly tagline?: string;
  /** Override the entire trigger button content (for icon / label variants). */
  readonly triggerContent?: ReactNode;
  /** Extra className on the trigger button (for size / style overrides). */
  readonly triggerClassName?: string;
  /** Floating placement — default "bottom-start". */
  readonly placement?: Placement;
  /** Typed sections. Dividers auto-inserted between adjacent sections. */
  readonly sections: readonly AppBrandMenuSection[];
  /** Rendered above sections (e.g. workspace label + status banner). */
  readonly header?: ReactNode;
  /** Rendered below sections (e.g. "drop files anywhere" hint). */
  readonly footer?: ReactNode;
  /** Extra className merged into the floating panel. */
  readonly panelClassName?: string;
}

// ── Styling helpers ───────────────────────────────────────────────────────────

const VARIANT_CLASS: Record<string, string> = {
  default:
    "text-slate-300 hover:bg-slate-800/60 hover:text-slate-100 focus-visible:bg-slate-800/60",
  accent:
    "text-arch-400 hover:bg-slate-800/60 focus-visible:bg-slate-800/60",
  danger:
    "text-red-400 hover:bg-red-500/10 hover:text-red-300 focus-visible:bg-red-500/10",
};

const ITEM_BASE =
  "flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none";

// ── Component ─────────────────────────────────────────────────────────────────

export function AppBrandMenu({
  // The product name is not threaded through here at all — AppWordmark owns it
  // outright. A pass-through would just be a second door onto the same bug.
  tagline,
  triggerContent,
  triggerClassName,
  placement = "bottom-start",
  sections,
  header,
  footer,
  panelClassName,
}: AppBrandMenuProps) {
  return (
    <Menu placement={placement}>
      <MenuButton
        className={
          triggerClassName ??
          "group flex select-none items-center gap-1 rounded-md px-2 py-1 transition-colors hover:bg-slate-800/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400"
        }
      >
        {triggerContent ?? (
          <>
            <AppWordmark tagline={tagline} />
            <ChevronDown
              className="ml-0.5 shrink-0 text-slate-500 transition-colors group-hover:text-slate-300"
              width={10}
              height={10}
              strokeWidth={1.5}
              aria-hidden
            />
          </>
        )}
      </MenuButton>

      <MenuItems
        className={cx(
          "au-panel-in z-overlay min-w-[260px] overflow-hidden rounded-lg border border-slate-700 bg-slate-900/95 py-1 shadow-xl shadow-black/40 backdrop-blur outline-none",
          panelClassName,
        )}
      >
        {header}

        {sections.map((section, si) => (
          <Fragment key={si}>
            {si > 0 && <div className="my-1 border-t border-slate-700/80" />}

            {section.label != null && (
              <p className="flex items-center gap-1.5 px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                {section.label}
              </p>
            )}

            {section.items.map((item, ii) => {
              if (item.kind === "custom") {
                return <Fragment key={item.key}>{item.render()}</Fragment>;
              }

              const key = item.key ?? `${si}-${ii}`;
              const variant = item.variant ?? "default";
              const variantClass = VARIANT_CLASS[variant] ?? VARIANT_CLASS.default;

              return (
                <MenuItem
                  key={key}
                  onClick={item.onClick}
                  danger={variant === "danger"}
                  disabled={item.disabled}
                  keepOpen={item.keepOpen}
                  className={cx(ITEM_BASE, variantClass)}
                >
                  {item.icon != null && (
                    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center opacity-70">
                      {item.icon}
                    </span>
                  )}
                  <span className="flex-1 truncate text-left">{item.label}</span>
                  {item.right != null && (
                    <span className="shrink-0">{item.right}</span>
                  )}
                </MenuItem>
              );
            })}
          </Fragment>
        ))}

        {footer}
      </MenuItems>
    </Menu>
  );
}
